import { useEffect, useId, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLang, type Key } from '../i18n'
import { api, type AgentId, type InstallResult } from '../lib/api'

type CtaState = 'idle' | 'confirm' | 'running' | 'done' | 'failed'

/** The agents the passerelle can install (agent-install.ts#INSTALLABLE_AGENTS). */
export type InstallableAgent = Extract<AgentId, 'claude' | 'copilot'>

/** The strings that name the agent; every other step shares the claude_install_* keys. */
const AGENT_KEYS: Record<InstallableAgent, { title: Key; body: Key; done: Key }> = {
  claude: { title: 'claude_install_confirm_title', body: 'claude_install_confirm_body', done: 'claude_install_done_version' },
  copilot: { title: 'copilot_install_confirm_title', body: 'copilot_install_confirm_body', done: 'copilot_install_done_version' },
}

/** Tail kept on screen even though the server already caps the captured log at 8000 chars — a
 *  failed installer's useful line (the actual error) is almost always at the END. */
const LOG_TAIL_CHARS = 2000

/**
 * The assisted-install affordance shown on AiAgentSection's Claude or Copilot card when that CLI
 * is not detected. A small state machine, one step of consent ahead of anything running on the
 * member's machine: idle (an "Install" button) → confirm (what Niobe is about to run, spelled out,
 * plus the literal command) → running (poll the job) → done | failed.
 *
 * `command` is that literal command, and it comes from the passerelle (`GET /agent/status` →
 * `installCommands[agent]`) rather than from here: it is the member's own OS that decides it, and
 * it has to be *exactly* what the passerelle will run, not a lookalike. Undefined — an older
 * passerelle that does not send it — shows no command rather than a guess.
 */
export function ClaudeInstallCta({
  agent = 'claude',
  onInstalled,
  command,
}: {
  agent?: InstallableAgent
  onInstalled: () => void
  command?: string
}) {
  const { t } = useLang()
  const keys = AGENT_KEYS[agent]
  const [state, setState] = useState<CtaState>('idle')
  const [jobId, setJobId] = useState<string | null>(null)
  /** Only set when triggerInstall() itself throws (no job was ever created to poll for a result). */
  const [triggerError, setTriggerError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const logId = useId()

  const jobQuery = useQuery({
    queryKey: ['agent-job', jobId],
    queryFn: () => api.agentJob(jobId as string),
    enabled: state === 'running' && !!jobId,
    refetchInterval: 2000,
  })
  const job = jobQuery.data
  const result = job?.result as InstallResult | undefined

  useEffect(() => {
    if (state !== 'running' || !job) return
    if (job.status === 'cancelled') {
      // The member asked to stop — back to idle, not an error state.
      setState('idle')
      setJobId(null)
    } else if (job.status === 'done') {
      // 'done' only means installAgent() resolved WITHOUT throwing — it still needs to be asked
      // whether the install itself actually worked. A resolved-but-unsuccessful InstallResult
      // (bad exit code, verifyAgent still finding nothing) is a failure, not a success, even
      // though the job's own status is 'done'.
      if (result?.ok) {
        setState('done')
        onInstalled()
      } else {
        setState('failed')
      }
    } else if (job.status === 'error') {
      setState('failed')
    }
  }, [state, job, result, onInstalled])

  async function confirmInstall(): Promise<void> {
    try {
      const { jobId: id } = await api.triggerInstall(agent)
      setJobId(id)
      setState('running')
    } catch (e) {
      setTriggerError(e instanceof Error ? e.message : String(e))
      setState('failed')
    }
  }

  async function cancelInstall(): Promise<void> {
    if (jobId) await api.cancelJob(jobId)
  }

  async function copyCommand(): Promise<void> {
    if (!command) return
    try {
      await navigator.clipboard.writeText(command)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      /* clipboard unavailable — the command text is still selectable on screen */
    }
  }

  if (state === 'idle') {
    return (
      <button className="mini ok" onClick={() => setState('confirm')}>
        {t('claude_install_cta')}
      </button>
    )
  }

  if (state === 'confirm') {
    return (
      <div className="claude-install" role="status" aria-live="polite">
        <p className="claude-install-title">
          <b>{t(keys.title)}</b>
        </p>
        <p>{t(keys.body)}</p>
        {command && <code className="mono claude-install-cmd">{command}</code>}
        <div className="claude-install-actions">
          <button className="mini ok" onClick={() => void confirmInstall()}>
            {t('claude_install_confirm_yes')}
          </button>
          <button className="mini ghost" onClick={() => setState('idle')}>
            {t('claude_install_confirm_cancel')}
          </button>
        </div>
      </div>
    )
  }

  if (state === 'running') {
    // The installer's own script echoes step-by-step lines (curl runs silent, so this is never
    // curl's own progress meter) — streamed through the same job.progress channel every other
    // job kind already uses. Falls back to the generic label before the first line arrives
    // (during the initial download, or if the install is otherwise silent).
    const latestLine = job?.progress?.at(-1)?.target
    return (
      <div className="claude-install" role="status" aria-live="polite">
        <span className="thinking">
          <span className="dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          {t('claude_install_running')}
        </span>
        {latestLine && <p className="claude-install-progress mono">{latestLine}</p>}
        <div className="claude-install-actions">
          <button className="mini ghost" onClick={() => void cancelInstall()}>
            {t('stop_generating')}
          </button>
        </div>
      </div>
    )
  }

  if (state === 'done') {
    return (
      <div className="claude-install" role="status" aria-live="polite">
        <p className="claude-install-success">
          {t(keys.done).replace('{n}', () => String(result?.version ?? '?'))}
        </p>
        <p className="settings-note">{t('claude_install_restart_hint')}</p>
      </div>
    )
  }

  // state === 'failed'
  const log = (result?.log ?? triggerError ?? '').slice(-LOG_TAIL_CHARS)
  return (
    <div className="claude-install" role="status" aria-live="polite">
      <p className="claude-install-failed-title">
        <b>{t('claude_install_failed')}</b>
      </p>
      {log !== '' && (
        <>
          <p id={logId} className="claude-install-log-label">
            {t('claude_install_log_label')}
          </p>
          <pre className="mono claude-install-log" tabIndex={0} aria-labelledby={logId}>
            {log}
          </pre>
        </>
      )}
      {/* Nothing to offer manually without the passerelle's command — an invented one would be a
          worse answer than none, since it is the member's own OS that decides it. */}
      {command && (
        <>
          <p>{t('claude_install_manual_label')}</p>
          <div className="claude-install-actions">
            <code className="mono claude-install-cmd">{command}</code>
            <button className="mini ok" onClick={() => void copyCommand()}>
              {copied ? t('ext_copied') : t('ext_copy')}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
